import React, { useMemo } from 'react'
import {
    Box,
    Typography,
    Button,
    Chip,
    Paper,
    Divider,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    CheckCircle as CheckCircleIcon,
    Edit as EditIcon,
} from '@material-ui/icons'
import { Attribute } from '../../types'

import { useAttributesTabHeaderStyles } from './styles'

interface AttributesTabHeaderProps {
    isEditMode: boolean
    attributes: Attribute[]
    isCompletingAttributeSelection: boolean
    onCompleteSelection: () => void
    onToggleEditMode: () => void
}

export const AttributesTabHeader: React.FC<AttributesTabHeaderProps> = ({
    isEditMode,
    attributes,
    isCompletingAttributeSelection,
    onCompleteSelection,
    onToggleEditMode,
}) => {
    const classes = useAttributesTabHeaderStyles()

    // Find unmatched attributes (where should_display is null)
    const unmatchedAttributes = useMemo(() => {
        return attributes.filter(
            (attr) => (attr.should_display as any) === null,
        )
    }, [attributes])

    return (
        <>
            <Box className={classes.headerContainer}>
                {/* Top Row: Legend and Unmatched Attributes */}
                <Box className={classes.topRow}>
                    {/* Legend Section */}
                    <Paper className={classes.legendContainer} elevation={0}>
                        <Typography className={classes.legendTitle}>
                            Legend
                        </Typography>
                        <Box className={classes.legendItem}>
                            <Chip
                                label="Extracting"
                                size="small"
                                className={`${classes.extractBadge} ${classes.legendChip}`}
                            />
                            <Typography className={classes.legendDescription}>
                                Attribute will be extracted from lease documents
                                using AI
                            </Typography>
                        </Box>
                        <Box className={classes.legendItem}>
                            <Chip
                                label="Displaying"
                                size="small"
                                className={`${classes.displayBadge} ${classes.legendChip}`}
                            />
                            <Typography className={classes.legendDescription}>
                                Attribute will be shown in the audit table
                            </Typography>
                        </Box>
                        <Box className={classes.legendItem}>
                            <Chip
                                label="Financial"
                                size="small"
                                className={`${classes.financialBadge} ${classes.legendChip}`}
                            />
                            <Typography className={classes.legendDescription}>
                                Currency attribute used in variance analysis
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Unmatched Attributes Note */}
                    {unmatchedAttributes.length > 0 && (
                        <Paper className={classes.unmatchedNote} elevation={0}>
                            <Typography className={classes.unmatchedTitle}>
                                ⚠ Unmatched Attributes Detected
                            </Typography>
                            <Typography
                                className={classes.unmatchedDescription}
                            >
                                The following attributes from your rent roll did
                                not match our predefined attributes:
                            </Typography>
                            <ul className={classes.unmatchedList}>
                                {unmatchedAttributes.map((attr) => (
                                    <li
                                        key={attr.id}
                                        className={classes.unmatchedListItem}
                                    >
                                        {attr.name}
                                    </li>
                                ))}
                            </ul>
                            <Typography className={classes.unmatchedFooter}>
                                To extract or view this data, please manually
                                configure them using the Edit Attributes button.
                            </Typography>
                        </Paper>
                    )}
                </Box>

                {/* Action Buttons */}
                <Box className={classes.buttonSection}>
                    {!isEditMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onCompleteSelection}
                            disabled={isCompletingAttributeSelection}
                            size="medium"
                            startIcon={<CheckCircleIcon />}
                        >
                            {isCompletingAttributeSelection
                                ? 'Completing...'
                                : 'Complete Attribute Selection'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onToggleEditMode}
                        size="medium"
                        startIcon={<EditIcon />}
                    >
                        {isEditMode ? 'Done Editing' : 'Edit Attributes'}
                    </Button>
                </Box>
            </Box>

            <Divider style={{ margin: '16px 0' }} />
        </>
    )
}
